import React from 'react'

import { useUpdateLog } from 'utils/log'

import { FormTitleProps } from '../items/FormTitle'
import SelectImageLine from '../items/SelectImageLine'
import ContentHeaderArea from 'components/templates/RequestTemplate/ContentHeaderArea'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import BaseGroup from './BaseGroup'

import { AccidentImage, AccidentImageKeys, ImageInfo } from 'states/QnState'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { useEditQnState } from 'utils/commonHooks'
import AccidentDefaultImage1 from 'images/accident-default-image1.png'
import AccidentDefaultImage2 from 'images/accident-default-image2.png'
import AccidentDefaultImage3 from 'images/accident-default-image3.png'
import AccidentDefaultImage4 from 'images/accident-default-image4.jpeg'
import AccidentDefaultImage5 from 'images/accident-default-image5.png'
import styled from 'styled-components'

/**
 * 対象がSelectImageGroupProps型かチェックする
 * @param target チェック対象
 */
export const isSelectImageGroupProps = (
  target: any
): target is SelectImageGroupProps => {
  return target.images !== undefined && target.title !== undefined
}

export type SelectImageGroupProps = FormTitleProps & {
  accidentImage: AccidentImage
  onChanged: (name: AccidentImageKeys, imageInfo: ImageInfo) => void
}

/**
 * 損傷画像を入力するコンポーネント
 * @param props SelectImageGroupProps
 * @returns
 */
export default function SelectImageGroup(
  props: SelectImageGroupProps
): JSX.Element {
  const {
    title,
    description,
    addPhoto,
    isSinglePhoto,
    isOptional,
    isRequired,
    hideTitle,
    accidentImage,
    onChanged,
  } = props

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, image: string) => {
      if (onChanged) {
        onChanged(name, { ...accidentImage[name], image })
      }
    },
    [accidentImage]
  )

  const handleOrientationChanged = React.useCallback(
    (name: AccidentImageKeys, orientation: number) => {
      if (onChanged) {
        onChanged(name, { ...accidentImage[name], orientation })
      }
    },
    [accidentImage]
  )

  useUpdateLog(
    props.title,
    {
      title: props.title,
      isRequired: props.isRequired,
      accidentImage: props.accidentImage,
    },
    [props]
  )

  const [editState, , setVehicleStateInfo] = useEditQnState()
  /** 写真以外スイッチ変更時のコールバック */
  const handleNoOtherPhotoChanged = React.useCallback(
    (value: boolean): void => {
      setVehicleStateInfo({
        isAddPhoto: value,
      })
    },
    [setVehicleStateInfo]
  )

  return (
    <div style={{ height: 'auto' }}>
      {
        <BaseGroup
          title={title}
          description={description}
          hideTitle={hideTitle}
          hasField
        >
          <ImageLineSpace>
            <SelectImageLine
              image={accidentImage.peripheral.image}
              isOptional={isOptional}
              isRequired={isRequired}
              orientation={accidentImage.peripheral.orientation}
              defaultImage={AccidentDefaultImage1}
              name={VEHICLE_STATE_FORM.accidentImage.options.peripheral}
              isSupplement={false}
              setImage={(image): void =>
                handleImageChanged('peripheral', image)
              }
              setOrientation={(orientation): void => {
                handleOrientationChanged('peripheral', orientation)
              }}
            />
            {!isSinglePhoto && (
              <SelectImageLine
                image={accidentImage.damagedPart.image}
                isOptional={isOptional}
                isRequired={isRequired}
                orientation={accidentImage.damagedPart.orientation}
                defaultImage={AccidentDefaultImage2}
                name={VEHICLE_STATE_FORM.accidentImage.options.damagedPart}
                isSupplement={false}
                setImage={(image): void =>
                  handleImageChanged('damagedPart', image)
                }
                setOrientation={(orientation): void => {
                  handleOrientationChanged('damagedPart', orientation)
                }}
              />
            )}
          </ImageLineSpace>
          <AddPhotoSpace>
            <SwitchButtonGroup
              title={`${VEHICLE_STATE_FORM.isAddPhoto.formName}を追加`}
              isSwitched={editState.vehicleStateInfo.isAddPhoto}
              label={`${VEHICLE_STATE_FORM.isAddPhoto.label}を追加`}
              onChanged={handleNoOtherPhotoChanged}
              isOption={true}
            />
            {addPhoto && (
              <>
                <ContentHeaderArea
                  description={
                    '迅速なかけつけのため、以下のような情報撮影にご協力ください。任意となりますので写真撮影が困難な場合はそのままお進みください。'
                  }
                />
              </>
            )}
          </AddPhotoSpace>
          {addPhoto && (
            <ImageLineSpace>
              <SelectImageLine
                image={accidentImage.vehicleCloseUp.image}
                isRequired={isRequired}
                orientation={accidentImage.vehicleCloseUp.orientation}
                defaultImage={AccidentDefaultImage3}
                name={VEHICLE_STATE_FORM.accidentImage.options.vehicleCloseUp}
                isSupplement={false}
                setImage={(image): void =>
                  handleImageChanged('vehicleCloseUp', image)
                }
                setOrientation={(orientation): void => {
                  handleOrientationChanged('vehicleCloseUp', orientation)
                }}
              />
              <SelectImageLine
                image={accidentImage.landmark.image}
                isRequired={isRequired}
                orientation={accidentImage.landmark.orientation}
                defaultImage={AccidentDefaultImage4}
                name={VEHICLE_STATE_FORM.accidentImage.options.landmark}
                isSupplement={false}
                setImage={(image): void =>
                  handleImageChanged('landmark', image)
                }
                setOrientation={(orientation): void => {
                  handleOrientationChanged('landmark', orientation)
                }}
              />
              <SelectImageLine
                image={accidentImage.carVerification.image}
                isRequired={isRequired}
                orientation={accidentImage.carVerification.orientation}
                defaultImage={AccidentDefaultImage5}
                name={VEHICLE_STATE_FORM.accidentImage.options.carVerification}
                isSupplement={false}
                setImage={(image): void =>
                  handleImageChanged('carVerification', image)
                }
                setOrientation={(orientation): void => {
                  handleOrientationChanged('carVerification', orientation)
                }}
              />
              <SelectImageLine
                image={accidentImage.supplement.image}
                isRequired={isRequired}
                orientation={accidentImage.supplement.orientation}
                name={VEHICLE_STATE_FORM.accidentImage.options.supplement}
                isSupplement={true}
                setImage={(image): void =>
                  handleImageChanged('supplement', image)
                }
                setOrientation={(orientation): void => {
                  handleOrientationChanged('supplement', orientation)
                }}
              />
            </ImageLineSpace>
          )}
        </BaseGroup>
      }
    </div>
  )
}

const ImageLineSpace = styled.div`
  background: #ffffff;
  padding: 0 0 10px 0;
`

const AddPhotoSpace = styled.div`
  padding: 0 0 10px 0;
`
